import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Radio do Design System (ADR-0065), o `.radio` do pacote: o input nativo fica
 * acessível porém invisível, e o ponto de 16px ao lado é o que se vê — marcado,
 * vira preenchimento em aço com anel interno na cor do ground.
 *
 * O input nativo é preservado (não é `div` com `role`), então teclado, formulário
 * e leitor de tela funcionam sem reimplementação.
 */

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  children: ReactNode;
  /** Classe do rótulo externo (o `<label>` que embrulha input e ponto). */
  wrapperClassName?: string;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { children, className, wrapperClassName, disabled, ...props },
  ref,
) {
  return (
    <label
      className={cn(
        'group relative inline-flex cursor-pointer items-center gap-2 text-[14px] text-fg',
        disabled && 'cursor-not-allowed opacity-60',
        wrapperClassName,
      )}
    >
      <input
        ref={ref}
        type="radio"
        disabled={disabled}
        className={cn('peer pointer-events-none absolute h-0 w-0 opacity-0', className)}
        {...props}
      />
      {/*
        Única exceção ao raio zero em todo o sistema, e ela vem do pacote: o
        `.radio .dot` é `border-radius: 50%`. O utilitário de pílula do Tailwind
        não serve — a escala do config resolve para 0 —, então o círculo precisa
        do valor literal.
      */}
      <span
        aria-hidden
        data-radio-dot
        className="h-4 w-4 flex-none rounded-[50%] border-[1.5px] border-border transition-colors duration-fast ease-sc group-hover:border-accent peer-checked:border-accent peer-checked:bg-accent peer-checked:shadow-[inset_0_0_0_4px_oklch(var(--bg))] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent"
      />
      {children}
    </label>
  );
});
